/**
 * Router-shell para a area /configuracoes/acessos.
 *
 * Estrutura (P3 — foco em tarefa, nao em entidade de banco):
 *   /configuracoes/acessos                  -> Hub overview
 *   /configuracoes/acessos/usuarios         -> Lista
 *   /configuracoes/acessos/usuarios/:id     -> Ficha em 3 zonas
 *   /configuracoes/acessos/cargos           -> Lista
 *   /configuracoes/acessos/cargos/:id       -> Ficha
 *   /configuracoes/acessos/auditoria        -> Timeline + drawer
 *   /configuracoes/acessos/conflitos-sod    -> CRUD + violacoes
 *
 * Sprint C da migracao RBAC: a sub-rota /configuracoes/acessos/legado e a env-var
 * REACT_APP_ACESSOS_V2_ENABLED foram removidas. Toda gestao acontece
 * exclusivamente pela tela nova `/configuracoes/acessos/usuarios` + `/configuracoes/acessos/cargos`.
 */
import React from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";

// AcessosKeyboardShortcuts agora vive em src/App.jsx (AuthenticatedShell) —
// atalhos sao globais a partir do Sprint 6 (debito tecnico).
import HubAcessos from "./Hub";
import UsuariosLista from "./Usuarios/Lista";
import UsuarioDetalhe from "./Usuarios/Detalhe";
import UsuarioNovo from "./Usuarios/Novo";
import CargosLista from "./Cargos/Lista";
import CargoDetalhe from "./Cargos/Detalhe";
import CargoNovo from "./Cargos/Novo";
import AuditoriaPagina from "./Auditoria";
import SodPagina from "./Sod";
import { PERMS_ABA_ACESSOS } from "./permissoes";
import { PageContainer, PageState, PageTitle, PageTopBar } from "../../styles/shared";

import { SubNav, SubNavLink } from "./styles.jsx";
import { useAuth } from "../../context/AuthContext";

const ABAS = [
  { path: "/configuracoes/acessos", label: "Visão geral", requireAny: PERMS_ABA_ACESSOS.hub },
  { path: "/configuracoes/acessos/usuarios", label: "Usuários", requireAny: PERMS_ABA_ACESSOS.usuarios },
  { path: "/configuracoes/acessos/cargos", label: "Cargos", requireAny: PERMS_ABA_ACESSOS.cargos },
  { path: "/configuracoes/acessos/auditoria", label: "Auditoria", requireAny: PERMS_ABA_ACESSOS.auditoria },
  { path: "/configuracoes/acessos/conflitos-sod", label: "Conflitos SoD", requireAny: PERMS_ABA_ACESSOS.sod },
];

function useAbasVisiveis() {
  const { hasAnyPermission } = useAuth();
  return ABAS.filter((aba) => hasAnyPermission(aba.requireAny));
}

function AcessosShell() {
  const navigate = useNavigate();
  const location = useLocation();
  // Cada aba aparece para quem tem a permissão dela (o Master vê todas).
  const abasVisiveis = useAbasVisiveis();

  const ativoFor = (path) => {
    if (path === "/configuracoes/acessos") {
      return location.pathname === "/configuracoes/acessos" || location.pathname === "/configuracoes/acessos/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <PageContainer>
      <PageTopBar>
        <PageTitle>Gestão de acessos</PageTitle>
      </PageTopBar>

      <SubNav role="tablist" aria-label="Sub-navegacao de acessos">
        {abasVisiveis.map((aba) => (
          <SubNavLink
            key={aba.path}
            type="button"
            role="tab"
            $active={ativoFor(aba.path)}
            aria-selected={ativoFor(aba.path)}
            onClick={() => navigate(aba.path)}
          >
            {aba.label}
          </SubNavLink>
        ))}
      </SubNav>

      <Outlet />
    </PageContainer>
  );
}

function SemAcesso() {
  return (
    <PageState $tone="error">
      Você não tem permissão para esta parte de Acessos.
    </PageState>
  );
}

/** Visão geral para quem cuida de usuários/cargos/SoD; senão, a 1ª aba liberada. */
function IndiceAcessos() {
  const { hasAnyPermission } = useAuth();
  const abas = useAbasVisiveis();
  if (hasAnyPermission(PERMS_ABA_ACESSOS.hub)) return <HubAcessos />;
  if (abas.length) return <Navigate to={abas[0].path} replace />;
  return <SemAcesso />;
}

/** A rota só abre com a permissão da tela (mesma regra da aba). */
function Exige({ codigos, children }) {
  const { hasAnyPermission } = useAuth();
  return hasAnyPermission(codigos) ? children : <SemAcesso />;
}


export default function Acessos() {
  return (
    <Routes>
      <Route element={<AcessosShell />}>
        <Route index element={<IndiceAcessos />} />
        <Route path="usuarios" element={<Exige codigos={PERMS_ABA_ACESSOS.usuarios}><UsuariosLista /></Exige>} />
        <Route path="usuarios/novo" element={<Exige codigos={["usuario:create"]}><UsuarioNovo /></Exige>} />
        <Route path="usuarios/:usuarioId" element={<Exige codigos={PERMS_ABA_ACESSOS.usuarios}><UsuarioDetalhe /></Exige>} />
        <Route path="cargos" element={<Exige codigos={PERMS_ABA_ACESSOS.cargos}><CargosLista /></Exige>} />
        <Route path="cargos/novo" element={<Exige codigos={["cargo:create"]}><CargoNovo /></Exige>} />
        <Route path="cargos/:cargoId" element={<Exige codigos={PERMS_ABA_ACESSOS.cargos}><CargoDetalhe /></Exige>} />
        <Route path="auditoria" element={<Exige codigos={PERMS_ABA_ACESSOS.auditoria}><AuditoriaPagina /></Exige>} />
        <Route path="conflitos-sod" element={<Exige codigos={PERMS_ABA_ACESSOS.sod}><SodPagina /></Exige>} />
        {/* Sprint C: a antiga sub-rota /configuracoes/acessos/legado foi descontinuada.
            Tentativas de acesso redirecionam para o Hub. */}
        <Route path="legado" element={<Navigate to="/configuracoes/acessos" replace />} />
        <Route path="legado/*" element={<Navigate to="/configuracoes/acessos" replace />} />
      </Route>
    </Routes>
  );
}
